<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { marked } from "marked";
import { useLocale } from "@/composables/useLocale";
import type { EventDetail, JoinInlineError } from "@/types/gate";

const props = withDefaults(
  defineProps<{
    event: EventDetail;
    submitting: boolean;
    inlineError: JoinInlineError | null;
    /** When true the card renders without any footer CTAs. The page is
     *  showing a separate action card (e.g. the paywall) below. */
    paywalled?: boolean;
  }>(),
  { paywalled: false },
);

const emit = defineEmits<{ join: []; openApp: [] }>();

const { t } = useI18n();
const { locale } = useLocale();

const now = ref(Date.now());
let intervalId: ReturnType<typeof setInterval> | null = null;

// Measured height of the fixed footer so the body's bottom padding matches
// exactly (no extra whitespace when only the primary CTA renders, no
// occlusion when both CTAs render).
const footerRef = ref<HTMLElement | null>(null);
const footerHeight = ref(96);
let footerObserver: ResizeObserver | null = null;

onMounted(() => {
  intervalId = setInterval(() => {
    now.value = Date.now();
  }, 1000);

  if (typeof ResizeObserver !== "undefined" && footerRef.value) {
    footerObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) footerHeight.value = entry.contentRect.height;
    });
    footerObserver.observe(footerRef.value);
  }
});

onUnmounted(() => {
  if (intervalId !== null) clearInterval(intervalId);
  footerObserver?.disconnect();
});

const isConfirmed = computed(() => props.event.userAttendanceStatus === "confirmed");

const joinable = computed(() => {
  if (props.event.status !== "live") return false;
  // Confirmed attendees already have a reserved spot, so `isFull` only
  // blocks walk-ins.
  if (props.event.isFull && !isConfirmed.value) return false;
  return true;
});

const disabled = computed(() => !joinable.value || props.submitting);

function formatTimeUntil(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (days > 0 || hours > 0) parts.push(`${hours}h`);
  if (days > 0 || hours > 0 || minutes > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);
  return parts.join(" ");
}

const notLiveLabel = computed(() => {
  const scheduledMs = Date.parse(props.event.scheduledAt);
  if (!Number.isFinite(scheduledMs)) return t("preview.cta.startingSoon");
  const diff = scheduledMs - now.value;
  // Scheduled time reached but backend hasn't flipped `live` yet.
  if (diff <= 0) return t("preview.cta.startingSoon");
  return t("preview.cta.startsIn", { time: formatTimeUntil(diff) });
});

const label = computed(() => {
  if (joinable.value) return t("preview.cta.join");

  // Terminal statuses take priority over `isFull`: a finished-and-full event
  // should read "Event ended", not "Event is full".
  if (props.event.status === "finished" || props.event.status === "participants_synced") {
    return t("preview.cta.finished");
  }
  if (props.event.status === "cancelled" || props.event.status === "interrupted") {
    return t("preview.cta.cancelled");
  }

  if (props.event.isFull && !isConfirmed.value) return t("preview.cta.full");

  if (props.event.status === "published") return notLiveLabel.value;

  return t("preview.cta.full");
});

const requirementsHtml = computed(() =>
  props.event.requirements.map((item) => marked.parseInline(item) as string),
);

const scheduleLine = computed(() => {
  const date = new Date(props.event.scheduledAt);
  if (Number.isNaN(date.getTime())) return props.event.scheduledAt;
  const formatted = new Intl.DateTimeFormat(locale.value, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(date);
  const duration = props.event.estimatedDuration;
  return duration ? `${formatted} · ${duration} min` : formatted;
});

function onJoin(): void {
  if (disabled.value) return;
  emit("join");
}

function onOpenApp(): void {
  emit("openApp");
}
</script>

<template>
  <section class="preview gate-card" aria-live="polite">
    <div
      class="preview__body"
      :style="paywalled ? undefined : { paddingBottom: `calc(${footerHeight}px + var(--space-5))` }"
    >
      <div class="preview__cover" :class="{ 'preview__cover--placeholder': !event.coverImageUrl }">
        <img
          v-if="event.coverImageUrl"
          :src="event.coverImageUrl"
          :alt="event.title"
          class="preview__cover-image"
        />
      </div>

      <h2 class="preview__title gate-heading">{{ event.title }}</h2>

      <p class="preview__schedule">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
          <rect
            x="3.5"
            y="5.5"
            width="17"
            height="15"
            rx="2"
            stroke="currentColor"
            stroke-width="1.6"
          />
          <path
            d="M3.5 10h17M8 3.5v4M16 3.5v4"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          />
        </svg>
        <time :datetime="event.scheduledAt">{{ scheduleLine }}</time>
      </p>

      <ul v-if="event.hosts.length > 0" class="preview__hosts">
        <li v-for="host in event.hosts" :key="host.hostId" class="preview__host">
          <img
            v-if="host.imageUrl"
            :src="host.imageUrl"
            :alt="host.name"
            class="preview__host-avatar"
            width="40"
            height="40"
          />
          <span v-else class="preview__host-avatar preview__host-avatar--placeholder">
            {{ host.name.charAt(0) }}
          </span>
          <span class="preview__host-text">
            <span class="preview__host-name">{{ host.name }}</span>
            <span v-if="host.title" class="preview__host-title">{{ host.title }}</span>
          </span>
        </li>
      </ul>

      <div v-if="event.accent || event.levels.length > 0" class="preview__chips">
        <span v-if="event.accent" class="preview__chip preview__chip--accent">
          <span
            :class="['fi', `fi-${event.accent.flag.toLowerCase()}`]"
            class="preview__chip-flag"
            aria-hidden="true"
          />
          <span>{{ event.accent.label }}</span>
        </span>
        <span
          v-for="level in event.levels"
          :key="level.code"
          class="preview__chip preview__chip--level"
        >
          {{ level.label || level.code }}
        </span>
      </div>

      <p v-if="event.description" class="preview__description">{{ event.description }}</p>

      <section v-if="requirementsHtml.length > 0" class="preview__requirements">
        <h3 class="preview__section-heading">{{ t("preview.requirementsHeading") }}</h3>
        <ul class="preview__requirements-list">
          <!-- Content is admin-authored; CSP script-src 'self' blocks inline-script XSS. -->
          <!-- eslint-disable vue/no-v-html -->
          <li
            v-for="(html, index) in requirementsHtml"
            :key="index"
            class="preview__requirement"
            v-html="html"
          />
          <!-- eslint-enable vue/no-v-html -->
        </ul>
      </section>
    </div>

    <footer v-if="!paywalled" ref="footerRef" class="preview__footer">
      <div class="preview__footer-inner">
        <p v-if="inlineError" class="preview__inline-error" role="alert">
          {{ t(`preview.inlineError.${inlineError}`) }}
        </p>

        <button type="button" class="gate-button preview__cta" :disabled="disabled" @click="onJoin">
          <span v-if="submitting" class="gate-spinner" aria-hidden="true" />
          <span>{{ label }}</span>
        </button>

        <button
          v-if="!joinable"
          type="button"
          class="gate-button gate-button--ghost preview__secondary"
          @click="onOpenApp"
        >
          {{ t("preview.openApp") }}
        </button>
      </div>
    </footer>
  </section>
</template>

<style scoped>
.preview {
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}

.preview__body {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  /* Default bottom padding for the paywalled case (no footer below).
     The non-paywalled case overrides this inline with the measured
     footer height so the last content stays clear of the fixed bar. */
  padding-bottom: var(--space-6);
}

.preview__footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background-color: var(--color-bg-elevated);
  border-top: 1px solid var(--color-border-subtle);
  box-shadow: 0 -6px 24px rgba(14, 1, 36, 0.06);
  padding: var(--space-4) var(--space-4) calc(var(--space-4) + env(safe-area-inset-bottom, 0px));
  display: flex;
  justify-content: center;
}

.preview__footer-inner {
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.preview__cover {
  width: 100%;
  aspect-ratio: 16 / 9;
  background-color: var(--color-primary-muted);
}

.preview__cover--placeholder {
  background: linear-gradient(135deg, var(--color-primary-muted), var(--color-bg-secondary));
}

.preview__cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.preview__title {
  padding: 0 var(--space-6);
  margin-top: var(--space-3);
}

.preview__schedule {
  padding: 0 var(--space-6);
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.preview__schedule svg {
  color: var(--color-primary);
  flex-shrink: 0;
}

.preview__hosts {
  padding: 0 var(--space-6);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.preview__host {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.preview__host-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background-color: var(--color-primary-muted);
}

.preview__host-avatar--placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);
  font-weight: 600;
  font-size: var(--font-size-lg);
  text-transform: uppercase;
}

.preview__host-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.preview__host-name {
  font-size: var(--font-size-md);
  color: var(--color-text);
  font-weight: 500;
}

.preview__host-title {
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
}

.preview__chips {
  padding: 0 var(--space-6);
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.preview__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-full);
  font-size: var(--font-size-sm);
  line-height: 1.2;
}

.preview__chip--accent {
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  color: var(--color-text);
}

.preview__chip--level {
  background-color: var(--color-primary-muted);
  color: var(--color-primary);
  font-weight: 600;
}

.preview__chip-flag {
  width: 18px;
  height: 13px;
  border-radius: 2px;
  flex-shrink: 0;
}

.preview__description {
  padding: 0 var(--space-6);
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  line-height: 1.5;
  white-space: pre-line;
}

.preview__requirements {
  padding: 0 var(--space-6);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.preview__section-heading {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.preview__requirements-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.preview__requirement {
  position: relative;
  padding-left: var(--space-4);
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.preview__requirement::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--color-primary);
}

.preview__requirement :deep(a) {
  color: var(--color-primary);
  text-decoration: underline;
}

.preview__requirement :deep(strong) {
  color: var(--color-text);
  font-weight: 600;
}

.preview__requirement :deep(code) {
  font-family: var(--font-mono);
  font-size: 0.92em;
  padding: 0.1em 0.35em;
  background-color: var(--color-primary-soft);
  border-radius: var(--radius-sm);
}

.preview__inline-error {
  font-size: var(--font-size-sm);
  color: var(--color-error);
}

.preview__cta,
.preview__secondary {
  width: 100%;
}
</style>
